import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../../styles/dashboard/BotDetailsModal.css';

function BotDetailsModal({ isOpen, onClose, userId }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStreaming, setAiStreaming] = useState(false);

  useEffect(() => {
    let streamingInterval = null;
    
    if (isOpen && userId) {
      setLoading(true);
      setAiText('');
      setAiLoading(false);
      setAiStreaming(false);
      
      // שליפת פרטי הבוט מהשרת
      // ב-useEffect, בחלק של ה-fetch:
      fetch(`https://api.anti-bot.ai/api/bot-details/${userId}`)
        .then(res => res.json())
        .then(data => {
          console.log('🔍 Raw server response:', data); // ← הוסף את זה
          
          if (data.success) {
            console.log('🤖 AI explanation from server:', JSON.stringify(data.ai_explanation)); // ← והוסף את זה
            
            // ניקוי נוסף של ה-AI explanation בצד הלקוח
            if (data.ai_explanation) {
              data.ai_explanation = data.ai_explanation
                .replace(/undefined/gi, '')
                .replace(/null/gi, '')
                .replace(/None/gi, '')
                .trim();
              
              console.log('🧹 After client cleanup:', JSON.stringify(data.ai_explanation)); // ← והוסף את זה
            }
            
            setDetails(data);
            
            // התחל streaming של ההסבר AI
            if (data.ai_explanation) {
              streamingInterval = streamAiText(data.ai_explanation);
            }
          }
          setLoading(false);
        })
    }
    
    // 🔧 ניקוי ה-interval כשהקומפוננטה נפרדת או משתנה
    return () => {
      if (streamingInterval) {
        clearInterval(streamingInterval);
      }
    };
  }, [isOpen, userId]);

  // 🔧 פונקציה ל-streaming של הטקסט - מתוקנת
  const streamAiText = (fullText) => {
    if (!fullText || typeof fullText !== 'string') {
      console.log('❌ Invalid text for streaming:', fullText);
      return;
    }
    
    console.log('🌊 Starting to stream text:', JSON.stringify(fullText));
    
    setAiLoading(true);
    setAiStreaming(true);
    setAiText('');
    
    let currentIndex = 0;
    const chars = fullText.split('');
    
    console.log('📝 Characters array length:', chars.length);
    
    const interval = setInterval(() => {
      if (currentIndex >= chars.length) {
        console.log('✅ Streaming finished - reached end');
        clearInterval(interval);
        setAiStreaming(false);
        setAiLoading(false);
        return;
      }
      
      const currentChar = chars[currentIndex];
      if (currentChar === undefined) {
        console.log('❌ Found undefined character at index:', currentIndex);
        clearInterval(interval);
        setAiStreaming(false);
        setAiLoading(false);
        return;
      }
      
      setAiText(prev => prev + currentChar);
      currentIndex++;
    }, 30);
    
    return interval;
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal-backdrop') {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        {/* כפתור סגירה */}
        <button className="modal-close" onClick={onClose}>✕</button>

        {loading ? (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>טוען פרטים...</p>
          </div>
        ) : details ? (
          <>
            {/* כותרת */}
            <div className="modal-header">
              <h2>ניתוח הבוט - {details.user_info.name || details.user_info.username}</h2>
            </div>

            {/* ציון */}
            <div className="modal-section">
              <div className="bot-score-display">
                <span className="score-label">ציון בוט:</span>
                <span className="score-value">{details.bot_score}/100</span>
              </div>
            </div>

            {/* 🆕 הסבר AI עם Streaming */}
            {details.ai_explanation && (
              <div className="modal-section ai-explanation-section">
                <h3>ניתוח AI:</h3>
                <div className="ai-explanation-box">
                  {aiLoading && aiText === '' ? (
                    <div className="ai-loading">
                      <span>מנתח</span>
                      <span className="dots"></span>
                    </div>
                  ) : (
                    <div className={`ai-text ${aiStreaming ? 'typing' : ''}`}>
                      {aiText}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* פירוט הניקוד */}
            <div className="modal-section">
              <h3>פירוט הניקוד:</h3>
              <div className="breakdown-list">
                {details.breakdown.followers > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">
                      {details.user_info.followers_count === 0 
                        ? '• 0 עוקבים' 
                        : `• יחס עוקבים נמוך (${details.user_info.followers_count})`}
                    </span>
                    <span className="breakdown-points">+{details.breakdown.followers} נקודות</span>
                  </div>
                )}
                
                {details.breakdown.account_age > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">• חשבון חדש ({details.account_age_days} ימים)</span>
                    <span className="breakdown-points">+{details.breakdown.account_age} נקודות</span>
                  </div>
                )}
                
                {details.breakdown.username > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">• שם משתמש חשוד</span>
                    <span className="breakdown-points">+{details.breakdown.username} נקודות</span>
                  </div>
                )}
                
                {details.breakdown.profile_image > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">• תמונת פרופיל דיפולט</span>
                    <span className="breakdown-points">+{details.breakdown.profile_image} נקודות</span>
                  </div>
                )}
                
                {details.breakdown.description > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">• ללא תיאור</span>
                    <span className="breakdown-points">+{details.breakdown.description} נקודות</span>
                  </div>
                )}
                
                {details.breakdown.tweet_count > 0 && (
                  <div className="breakdown-item">
                    <span className="breakdown-reason">• מעט ציוצים ({details.user_info.tweet_count})</span>
                    <span className="breakdown-points">+{details.breakdown.tweet_count} נקודות</span>
                  </div>
                )}
              </div>
            </div>

            {/* פרטי המשתמש */}
            <div className="modal-section">
              <h3>פרטי המשתמש:</h3>
              <div className="user-details">
                <div className="detail-row">
                  <span className="detail-label">שם משתמש:</span>
                  <span className="detail-value">@{details.user_info.username}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">תאריך הצטרפות:</span>
                  <span className="detail-value">{details.user_info.created_at}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">עוקבים:</span>
                  <span className="detail-value">{details.user_info.followers_count.toLocaleString()}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">עוקב אחרי:</span>
                  <span className="detail-value">{details.user_info.following_count.toLocaleString()}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">ציוצים:</span>
                  <span className="detail-value">{details.user_info.tweet_count.toLocaleString()}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">תיאור:</span>
                  <span className="detail-value">
                    {details.user_info.description || 'אין תיאור'}
                  </span>
                </div>
              </div>
            </div>

            {/* כפתור סגירה */}
            <div className="modal-footer">
              <button className="modal-btn-close" onClick={onClose}>
                סגור
              </button>
            </div>
          </>
        ) : (
          <div className="modal-error">
            <p>לא ניתן לטעון פרטי בוט</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BotDetailsModal;